import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Container, Modal, Button, Card, Breadcrumb, Row, Col, Badge, Table } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import AlidaLoader from '../../components/common/AlidaLoader';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { formatDate, setObjectType } from '../../utils';
import { Datasets } from '../../api/Datasets';
import UserItem from '../../components/common/UserItem';
import AssetRibbon from '../../components/common/AssetRibbon';
import { QueryDetails, VizClient } from '../../api/VizClient';
import { BdaDataset } from '../../models/BdaDataset';
import { Flex, Tag, Tooltip } from 'antd';
import { DatabaseDatasource, ObjectStorageDatasource, RemoteDatasource } from '../../models/Datasource';
import { DataspaceConnector } from '../../models/DataSpace';
import DataspaceConnectorItem from '../../components/list-items/DataspaceConnectorItem';
import { DataRetriever } from '../../api/DataRetriever';
import AssetProjectsEditor from '../../components/forms/AssetProjectsEditor';
import DatasetPreviewModal from '../../components/generics/DatasetPreviewModal';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import FileSaver from 'file-saver';
import ScriptEditor from '../../components/common/ScriptEditor';
import AppLoader from '../../components/common/AppLoader';
import AssetCategory from '../../components/common/AssetCategory';

export default function DatasetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [datasetPreview, setDatasetPreview] = useState<any[]>();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [dataset, setDataset] = useState<BdaDataset>();
  const [connectors, setConnectors] = useState<DataspaceConnector[]>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const datasetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));
  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));

  const initConnectors = useCallback(() => {
    const alidaUrl = window.location.href;

    DataRetriever.getDataspaceConnectorsByUrl(alidaUrl)
      .then(
        ({ data }) => setConnectors(data),
        (err) => {
          setConnectors([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connectors-error', message, `${datasetLabels.singular} Connectors Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    // eslint-disable-next-line
  }, [showToast]);

  const initDataset = useCallback(() => {
    Datasets.getDataset(+id!)
      .then(
        ({ data: dataset }) => {
          setDataset(dataset);
          if (window.__env.DATASPACES_ENABLED) initConnectors();
        },
        () => {
          showToast('dataset-error', `Could not get ${datasetLabels.singular}.`, `${datasetLabels.singular} Error`, 'danger');
          setError(true);
        }
      );
    // eslint-disable-next-line
  }, [id, initConnectors, showToast]);

  useEffect(() => initDataset(), [initDataset]);

  const onEditDataset = useCallback(() => navigate(`/datasets/${id}/edit`), [id, navigate]);
  const onDeleteDataset = useCallback(() => setShowDeleteModal(true), []);
  const handleDeleteClose = useCallback(() => setShowDeleteModal(false), []);

  const onDatasetCopy = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(dataset, null, 2));
    showToast("dataset-copy", `${datasetLabels.singular} copied to clipboard`);
  }, [dataset, showToast, datasetLabels]);

  const handlePreviewClose = () => {
    setShowPreviewModal(false);
    setDatasetPreview(undefined);
  };

  const deleteDataset = () => {
    showLoader();

    Datasets.deleteDataset(dataset!.id!)
      .then(
        () => {
          showToast('dataset-delete-success', `${datasetLabels.singular} deleted successfully.`, 'Success', 'success');
          hideLoader();
          handleDeleteClose();
          navigate('/datasets');
        },
        () => {
          showToast('dataset-delete-error', `Could not delete ${datasetLabels.singular}.`, `${datasetLabels.singular} Error`, 'danger');
          hideLoader();
        }
      );
  };

  const openDatasetPreview = () => {
    setShowPreviewModal(true);
    const body = new QueryDetails(dataset!.datasource, dataset!.datasetFileType);

    VizClient.getDatasetPreview(body)
      .then(
        ({ data }) => setDatasetPreview(data || []),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `${datasetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const openProjectsModal = () => setShowProjectsModal(true);
  const handleProjectsModalClose = () => setShowProjectsModal(false);

  const navigateToPublish = useCallback(() => navigate('publish'), [navigate]);

  const downloadDataset = useCallback(() => {
    showLoader();
    const body = new QueryDetails(dataset!.datasource, dataset!.datasetFileType);

    VizClient.downloadFile(body)
      .then(
        ({ data }) => {
          hideLoader();
          const blob = new Blob([data], { type: 'application/zip' });
          FileSaver.saveAs(blob, `${dataset!.name}.zip`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasets-error', message, `${datasetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [dataset, showToast, datasetLabels, hideLoader, showLoader]);

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred during {datasetLabels.singular} retrieve. Try again later.</span>
      </Alert>
    );

    if (!dataset) return (
      <div className="d-flex justify-content-center py-5">
        <AlidaLoader size={300} />
      </div>
    );

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/datasets' }}>{datasetLabels.plural}</Breadcrumb.Item>
          <Breadcrumb.Item active>{dataset.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Card className="item-detail-group main bg-dataset">
          <div className="item-ribbons">
            {(dataset.createdByServiceId === null) && (
              <AssetRibbon
                icon={solid('user')}
                color="info"
                tooltip="This has been created by a user."
              />
            )}
            {(dataset.createdByServiceId !== null) && (
              <AssetRibbon
                icon={solid('cogs')}
                color="dark"
                tooltip="This is an output from an execution."
              />
            )}
            {!dataset.created && (
              <AssetRibbon
                icon={solid('lock')}
                color="warning"
                tooltip="This has not been created yet."
              />
            )}
          </div>

          <FontAwesomeIcon className="item-icon" icon={solid('database')} />

          <Flex justify="end">
            {dataset.category && <AssetCategory className="me-2 fw-bold shadow" category={dataset.category} solid />}
            <Tag color="blue" className="shadow text-uppercase font-xs fw-bold m-0">{dataset.accessLevel}</Tag>
            {dataset.deleteDateTime && (<Tag color="red" className="shadow text-uppercase font-xs fw-bold m-0 ms-2">Deprecated</Tag>)}
          </Flex>

          <div className="position-relative p-2 py-4">
            <Row className="gx-0 align-items-center justify-content-md-between">
              <Col xs={12} md="auto" className="pe-3">
                <h3 className="mb-0">{dataset.name}</h3>
                <p className="text-trailing mb-3">{dataset.description}</p>
              </Col>

              <Col xs={12} md="auto" className="text-center text-md-start">
                {!dataset.deleteDateTime && (
                  <>
                    <Tooltip title="Manage Projects">
                      <button className="circle-button" onClick={openProjectsModal}>
                        <span className="fa-layers">
                          <FontAwesomeIcon icon={solid('folder')} transform="left-14 grow-2" />
                          <FontAwesomeIcon icon={solid('cog')} transform="shrink-4 down-8 right-3" />
                        </span>
                      </button>
                    </Tooltip>
                    {window.__env.DATASPACES_ENABLED && (
                      <Tooltip title="Publish">
                        <button className="circle-button" onClick={navigateToPublish}>
                          <FontAwesomeIcon icon={solid('share-from-square')} />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="Copy to Clipboard">
                      <button className="circle-button" onClick={onDatasetCopy}>
                        <FontAwesomeIcon icon={solid('clipboard')} />
                      </button>
                    </Tooltip>
                    {dataset.datasource?.type === "objectStorage" && (
                      <Tooltip title="Download">
                        <button className="circle-button" disabled={!dataset.created} onClick={downloadDataset}>
                          <FontAwesomeIcon icon={solid('download')} />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <button className="circle-button" disabled={!dataset.created} onClick={onEditDataset}>
                        <FontAwesomeIcon icon={solid('pen')} />
                      </button>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <button className="circle-button" disabled={!dataset.created} onClick={onDeleteDataset}>
                        <FontAwesomeIcon icon={solid('trash')} />
                      </button>
                    </Tooltip>
                  </>
                )}
                {dataset.datasetFileType!.type === "table" && (
                  <Tooltip title="Preview">
                    <button className="circle-button" disabled={!dataset.created} onClick={openDatasetPreview}>
                      <FontAwesomeIcon icon={solid('eye')} />
                    </button>
                  </Tooltip>
                )}
              </Col>
            </Row>
          </div>

          <Row className="gx-0 justify-content-between">
            <Col xs="auto">
              <div className="bg-highlight">
                <UserItem className="negative" user={dataset.owner!} organization={dataset.organization!} />
              </div>
            </Col>

            {dataset.tags.length > 0 && (
              <Flex className="tags-wrapper w-auto my-2">
                {dataset.tags.map(tag => <Badge className="m-1" pill={true} key={tag}>{tag}</Badge>)}
              </Flex>
            )}
          </Row>

          <div className="item-dates">
            <div className="d-inline-block">
              <b>Created:</b>
              <span className="ms-1">{formatDate(dataset.createDateTime!)}</span>
            </div>
            <div className="d-inline-block ms-2">
              <b>Last Update:</b>
              <span className="ms-1">{formatDate(dataset.updateDateTime!)}</span>
            </div>
          </div>
        </Card>

        <Card className="item-detail-group mb-3">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('database')} />
            <h5 className="d-inline ms-2 mb-0">{datasetLabels.singular} Source</h5>
          </div>

          <Card.Body>
            <Table className="mb-0">
              <thead>
                <tr>
                  <th className="p-0 border-0" style={{ width: "40%" }}></th>
                  <th className="p-0 border-0"></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="col">Name</th>
                  <td className="text-end">
                    <Link to={`/datasources/${dataset.datasource!.id}`} target="_blank" rel="noopener noreferrer">{dataset.datasource!.name}</Link>
                  </td>
                </tr>
                <tr>
                  <th scope="col">Description</th>
                  <td className="text-end">{dataset.datasource!.description}</td>
                </tr>
                <tr>
                  <th scope="col">Driver</th>
                  <td className="text-end">{dataset.datasource!.driverType}</td>
                </tr>
                {dataset.datasource!.type === 'remote' && setObjectType<RemoteDatasource>(dataset.datasource)
                  ? (
                    <>
                      <tr>
                        <th scope="col">URL</th>
                        <td className="text-end">{dataset.datasource.url}</td>
                      </tr>
                      <tr>
                        <th scope="col">Auth</th>
                        <td className="text-end">
                          <ScriptEditor value={dataset.datasource.auth} height={250} readOnly />
                        </td>
                      </tr>
                    </>
                  )
                  : (
                    <>
                      {dataset.datasource!.host && (
                        <tr>
                          <th scope="col">Host</th>
                          <td className="text-end">{dataset.datasource!.host}</td>
                        </tr>
                      )}
                      {dataset.datasource!.port && (
                        <tr>
                          <th scope="col">Port</th>
                          <td className="text-end">{dataset.datasource!.port}</td>
                        </tr>
                      )}
                    </>
                  )}
                {dataset.datasource!.type === "objectStorage" && setObjectType<ObjectStorageDatasource>(dataset.datasource) && (
                  <tr>
                    <th scope="col">Bucket</th>
                    <td className="text-end">
                      <FontAwesomeIcon icon={solid('bucket')} size="sm" />
                      <span className="ms-2">{dataset.datasource.bucket}</span>
                    </td>
                  </tr>
                )}
                {dataset.datasource!.type === "database" && setObjectType<DatabaseDatasource>(dataset.datasource) && (
                  <tr>
                    <th scope="col">Database</th>
                    <td className="text-end">
                      <FontAwesomeIcon icon={solid('database')} size="sm" />
                      <span className="ms-2">{dataset.datasource.database}</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Card className="item-detail-group mb-3">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <h5 className="d-inline ms-2 mb-0">{datasetLabels.singular} Info</h5>
          </div>

          <Card.Body>
            <Table className="mb-0">
              <thead>
                <tr>
                  <th className="p-0 border-0" style={{ width: "40%" }}></th>
                  <th className="p-0 border-0"></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="col">Type</th>
                  <td className="text-end">{dataset.datasetFileType!.type}</td>
                </tr>
                {dataset.datasetFileType!.topic && (
                  <tr>
                    <th scope="col">Topic</th>
                    <td className="text-end">{dataset.datasetFileType!.topic}</td>
                  </tr>
                )}
                {dataset.datasetFileType!.group && (
                  <tr>
                    <th scope="col">Group</th>
                    <td className="text-end">{dataset.datasetFileType!.group}</td>
                  </tr>
                )}
                {dataset.datasetFileType!.tableName && (
                  <tr>
                    <th scope="col">Table Name</th>
                    <td className="text-end">{dataset.datasetFileType!.tableName}</td>
                  </tr>
                )}
                {dataset.datasetFileType!.path && (
                  <tr>
                    <th scope="col">Path</th>
                    <td className="text-end">{dataset.datasetFileType!.path}</td>
                  </tr>
                )}
                {dataset.datasetFileType!.delimiter && (
                  <tr>
                    <th scope="col">Delimiter</th>
                    <td className="text-end">{dataset.datasetFileType!.delimiter}</td>
                  </tr>
                )}
                {dataset.datasetFileType!.details && (
                  <tr>
                    <th scope="col">Details</th>
                    <td className="text-end">
                      <ScriptEditor value={dataset.datasetFileType!.details} height={250} readOnly />
                    </td>
                  </tr>
                )}
                {dataset.createdByBdaId && (
                  <tr>
                    <th className="align-middle" scope="col">Created by {applicationLabels.singular}</th>
                    <td className="text-end">
                      <Link className="d-inline-block" to={`/bda-applications/${dataset.createdByBdaId}`} target="_blank" rel="noopener noreferer">
                        <div className="border bg-light px-2 rounded">
                          <FontAwesomeIcon icon={solid('laptop-code')} />
                          <span className="ms-2">{dataset.createdByBdaId}</span>
                        </div>
                      </Link>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            {dataset.datasetFileType!.columns && (
              <>
                <h6 className="mt-3">{datasetLabels.singular} Columns</h6>
                <Table className="mb-0">
                  <tbody>
                    {dataset.datasetFileType!.columns.map(column => (
                      <tr key={column.name}>
                        <th scope="col">{column.name}</th>
                        <td className="text-end">
                          <code>{column.type}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Card.Body>
        </Card>

        {window.__env.DATASPACES_ENABLED && (
          <Card className="item-detail-group">
            <div className="item-detail-highlight">
              <FontAwesomeIcon icon={solid('store')} fixedWidth />
              <h5 className="d-inline ms-2 mb-0">Available at</h5>
            </div>

            <Card.Body className="p-0 pt-1 px-2">
              {!connectors && <AppLoader className="py-3" justify="center" size={60} />}

              {connectors?.length === 0
                ? (
                  <Alert variant="info" className="mt-2 p-2">
                    <FontAwesomeIcon icon={solid('info-circle')} />
                    <span className="ms-2">No offers found for this {datasetLabels.singular}.</span>
                  </Alert>
                )
                : (
                  <Row>
                    {connectors?.map((connector) => (
                      <Col key={connector.id} className="p-2" md={6} lg={4}>
                        <DataspaceConnectorItem className="h-100" connector={connector} actions={<></>} />
                      </Col>
                    ))}
                  </Row>
                )
              }
            </Card.Body>
          </Card>
        )}
      </>
    );
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>{datasetLabels.singular} {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <br />

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {datasetLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {datasetLabels.singular} <b>{dataset && dataset.name}</b>?</p>

          <Alert className="p-2" variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteDataset}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <DatasetPreviewModal
        show={showPreviewModal}
        onClose={handlePreviewClose}
        preview={datasetPreview}
        title={`${dataset?.name || datasetLabels.singular} Preview`}
      />

      <Modal show={showProjectsModal} onHide={handleProjectsModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{datasetLabels.singular} Projects</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AssetProjectsEditor assetId={+id!} assetType="dataset" assetLabel={datasetLabels.singular} />
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="light" className="border" onClick={handleProjectsModalClose} size="sm">Cancel</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
